import { useQuery } from '@tanstack/react-query';
import axiosClient from 'api/config/axios';
import { adaptDispute } from 'api/escrow/adapters';
import { Dispute } from 'api/escrow/types';
import { memoryQueryClient } from 'providers/TanstackQuery';
import { DisputeStatusEnum, TERMINAL_DISPUTE_STATUSES } from 'utils/enums';

async function getDisputeStatus(disputeId: string): Promise<Dispute> {
  const result = await axiosClient.query.get(`/api/v1/escrow/cases/${disputeId}/status`);
  return adaptDispute(result.data.dispute);
}

export const getDisputeStatusQueryKey = (disputeId: string) => ['escrow', 'dispute', disputeId] as const;

type Options = {
  enabled?: boolean;
  polling?: boolean;
};

export function useGetDisputeStatusQuery(disputeId: string, options: Options = {}) {
  const { enabled = true, polling = false } = options;

  return useQuery(
    {
      queryKey: getDisputeStatusQueryKey(disputeId),
      queryFn: () => getDisputeStatus(disputeId),
      enabled,
      // Poll every 15 seconds when active; auto-stop when dispute reaches a terminal state
      refetchInterval: polling
        ? (query) => {
            const data = query.state.data;
            if (data && TERMINAL_DISPUTE_STATUSES.includes(data.status as DisputeStatusEnum)) return false;
            return 15000;
          }
        : false,
      refetchIntervalInBackground: false,
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 403 || status === 401 || status === 404) return false;
        return failureCount < 3;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    },
    memoryQueryClient,
  );
}
